import React, { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, AlertTriangle, CheckCircle2, Activity } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import AppointmentDialog from "@/components/AppointmentDialog";
import useSEO from "@/hooks/useSEO";
import { conditions, conditionJsonLd } from "@/data/conditions";

export default function ConditionPage() {
  const { slug } = useParams();
  const c = conditions[slug];
  const [open, setOpen] = useState(false);

  useSEO({
    title: c?.seoTitle,
    description: c?.seoDescription,
    image: c?.image,
    jsonLd: c ? conditionJsonLd(c) : undefined,
  });

  if (!c) return <Navigate to="/general-psychiatry" replace />;

  return (
    <div data-testid={`condition-page-${c.slug}`}>
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-14">
        <nav className="text-sm text-[#A39171] mb-8 flex items-center gap-2 flex-wrap">
          <Link to="/" className="hover:text-[#E07A5F]">Home</Link> /
          <Link to="/general-psychiatry" className="hover:text-[#E07A5F]">General Psychiatry</Link> /
          <span className="text-[#5C524E]">{c.condName}</span>
        </nav>
        <p className="eyebrow text-[#A39171] mb-6">{c.eyebrow}</p>
        <h1 className="font-display text-5xl md:text-7xl text-[#2D2623] leading-[0.95] tracking-tight max-w-4xl">
          {c.h1a} <span className="italic text-[#E07A5F]">{c.h1b}</span>
        </h1>
        <p className="mt-8 text-xl text-[#5C524E] max-w-3xl leading-relaxed">{c.intro}</p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <Button data-testid="cond-hero-book" onClick={() => setOpen(true)} className="rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-[#FDFBF7] px-8 h-14 font-medium">
            Book an OPD Consultation <ArrowUpRight className="ml-1 w-4 h-4" />
          </Button>
          <a href="tel:+919427560101" data-testid="cond-hero-call" className="rounded-full border border-[#2D2623]/20 hover:border-[#E07A5F] hover:text-[#E07A5F] text-[#2D2623] px-8 h-14 inline-flex items-center justify-center font-medium transition-colors">
            Call +91 94275 60101
          </a>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 mb-24">
        <div className="rounded-3xl overflow-hidden aspect-[16/9] md:aspect-[21/9] relative">
          <img src={c.image} alt={c.imageAlt} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/90 via-[#1A1513]/45 to-transparent" />
          <div className="absolute bottom-8 left-8 right-8 md:left-12 md:right-auto md:max-w-xl">
            <p className="eyebrow text-[#F0CF5B] mb-3 drop-shadow-[0_2px_8px_rgba(0,0,0,0.65)]">Manas Neuropsychiatry Hospital · Bhavnagar</p>
            <p className="font-display text-2xl md:text-3xl leading-tight text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.7)]">
              {c.caption}
            </p>
          </div>
        </div>
      </section>

      {/* SYMPTOMS */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">Symptoms</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight mb-14 max-w-3xl">
            How it actually <span className="italic text-[#E07A5F]">shows up</span>.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {c.symptomGroups.map((g, i) => (
              <div key={g.label} data-testid={`cond-symptoms-${i}`}
                className="bg-[#F4EFE6] rounded-3xl p-8 md:p-10 border border-[#A39171]/15">
                <h3 className="font-display text-2xl md:text-3xl text-[#2D2623] tracking-tight mb-6">{g.label}</h3>
                <ul className="space-y-3">
                  {g.items.map((it) => (
                    <li key={it} className="flex items-start gap-3 text-[#5C524E]">
                      <Activity className="w-4 h-4 text-[#E07A5F] mt-1 shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* CAUSES */}
          <div className="mt-20 grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-5">
              <p className="eyebrow text-[#A39171] mb-4">Why it happens</p>
              <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight leading-tight">
                Causes we look for <span className="italic text-[#E07A5F]">before prescribing</span>.
              </h2>
            </div>
            <ul className="md:col-span-7 space-y-4">
              {c.causes.map((cause) => (
                <li key={cause} data-testid="cond-cause"
                  className="bg-[#FDFBF7] border border-[#A39171]/20 rounded-2xl px-6 py-5 flex items-start gap-3 text-[#2D2623]">
                  <CheckCircle2 className="w-4 h-4 text-[#E07A5F] mt-1 shrink-0" />
                  <span>{cause}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* TREATMENT */}
      <section className="bg-[#F4EFE6]/60 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">Our treatment approach</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight mb-14 max-w-3xl">
            What treatment at Manas <span className="italic text-[#E07A5F]">actually involves</span>.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {c.treatments.map((t, i) => (
              <div key={t.title} data-testid={`cond-treatment-${i}`}
                className="bg-[#FDFBF7] rounded-3xl p-8 border border-[#A39171]/15 hover:border-[#E07A5F]/40 transition-all flex flex-col">
                <span className="font-display text-3xl text-[#E07A5F] mb-4">0{i + 1}</span>
                <h3 className="font-display text-2xl text-[#2D2623] tracking-tight leading-tight">{t.title}</h3>
                <p className="text-[#5C524E] mt-3 text-sm leading-relaxed flex-1">{t.desc}</p>
                {t.link && (
                  <Link to={t.link.to} className="mt-5 inline-flex items-center gap-2 text-[#2D2623] hover:text-[#E07A5F] font-medium text-sm link-underline">
                    {t.link.label} <ArrowUpRight className="w-3.5 h-3.5" />
                  </Link>
                )}
              </div>
            ))}
          </div>

          <div data-testid="cond-urgent" className="mt-14 bg-[#2D2623] text-[#FDFBF7] rounded-3xl p-8 md:p-10 flex flex-col md:flex-row gap-6 items-start">
            <span className="w-12 h-12 rounded-2xl bg-[#E07A5F] flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5" />
            </span>
            <div>
              <p className="eyebrow text-[#E2C044] mb-3">Please read this</p>
              <p className="text-lg leading-relaxed text-[#FDFBF7]/90">{c.urgent}</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">Frequently asked</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight mb-14 max-w-3xl">
            Questions we are asked <span className="italic text-[#E07A5F]">every week</span>.
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {c.faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`f-${i}`} data-testid={`cond-faq-${i}`}
                className="bg-[#F4EFE6] rounded-2xl border border-[#A39171]/15 px-6 data-[state=open]:border-[#E07A5F]/35 transition-colors">
                <AccordionTrigger className="text-left font-display text-xl md:text-2xl text-[#2D2623] tracking-tight hover:no-underline py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#5C524E] text-base md:text-lg leading-relaxed pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* RELATED + CTA */}
      <section className="bg-[#2D2623] text-[#FDFBF7] py-20 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 relative grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-tight">
              Treatment begins with <span className="italic text-[#E07A5F]">one consultation</span>.
            </h2>
            <p className="text-[#FDFBF7]/70 mt-5 max-w-xl">
              OPD Mon – Sat, 9 AM – 8 PM at Kalanala, Bhavnagar. No referral needed. Emergency support 24×7.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Button data-testid="cond-cta-book" onClick={() => setOpen(true)} className="rounded-full bg-[#E07A5F] hover:bg-[#FDFBF7] hover:text-[#2D2623] text-white px-8 h-12 font-medium">Request Appointment</Button>
              <a href="tel:+919427560101" className="rounded-full border border-[#FDFBF7]/30 hover:bg-[#FDFBF7] hover:text-[#2D2623] px-8 h-12 inline-flex items-center justify-center font-medium transition-colors">Call +91 94275 60101</a>
            </div>
          </div>
          <div className="md:col-span-5 flex flex-col gap-3">
            <p className="eyebrow text-[#E2C044] mb-1">Related</p>
            {c.related.map((r) => (
              <Link key={r.to} to={r.to} data-testid={`cond-related-${r.to.replace(/\//g, "-")}`}
                className="rounded-2xl bg-[#FDFBF7]/5 border border-[#FDFBF7]/15 hover:bg-[#E07A5F] px-6 py-4 flex items-center justify-between transition-colors">
                <span className="font-medium">{r.label}</span>
                <ArrowUpRight className="w-4 h-4 shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <AppointmentDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
